import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Owner from '@ember/owner';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { eq } from 'ember-truth-helpers';
import type Session from '../services/session';
import type PushNotifications from '../services/push-notifications';
import type { LocationStatus } from '../services/location';
import type LocationService from '../services/location';
import { isNativePlatform } from '../utils/native-platform';
import ThemeSelector from '../components/theme-selector.gts';
import LanguageSelector from '../components/language-selector.gts';
import Icon from '../components/icon';
import MobileHeader from '../components/mobile-header.gts';
import LogOutIcon from '~icons/lucide/log-out';
import ShieldIcon from '~icons/lucide/shield';
import MailIcon from '~icons/lucide/mail';
import UserIcon from '~icons/lucide/user';
import LinkIcon from '~icons/lucide/link';
import UnlinkIcon from '~icons/lucide/unlink';
import TvIcon from '~icons/lucide/tv';
import BellIcon from '~icons/lucide/bell';
import MapPinIcon from '~icons/lucide/map-pin';
import { apiFetch } from '../fetch';
import GoogleIcon from '~icons/simple-icons/google';
import config from 'favoris-web/config/environment';
import { tmdbProviderLogo } from '../utils/external-urls';

interface LinkedAccountData {
  id: string;
  attributes: {
    platform: string;
    platformAccountId: string;
    platformUsername: string | null;
    platformDisplayName: string | null;
    linkedAt: string;
  };
}

interface OAuthAccount {
  id: string;
  providerId: string;
  accountId: string;
}

const PLATFORM_LABELS: Record<string, string> = {
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  tiktok: 'TikTok',
};

const SOCIAL_PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  github: 'GitHub',
  apple: 'Apple',
  facebook: 'Facebook',
  discord: 'Discord',
  twitter: 'Twitter',
};

const SOCIAL_PROVIDER_ICONS: Record<string, string> = {
  google: GoogleIcon,
};

function includesId(list: number[], id: number): boolean {
  return list.includes(id);
}

type PushStatus = 'loading' | 'unsupported' | 'blocked' | 'on' | 'off';
type AccountLocationStatus = LocationStatus | 'loading';

export default class MyAccountPage extends Component {
  @service declare session: Session;
  @service declare pushNotifications: PushNotifications;
  @service declare location: LocationService;

  @tracked pushStatus: PushStatus = 'loading';
  @tracked isTogglingPush = false;
  @tracked isNativePush = false;

  @tracked locationStatus: AccountLocationStatus = 'loading';
  @tracked isTogglingLocation = false;
  @tracked isNativeLocation = false;

  @tracked linkedAccounts: LinkedAccountData[] = [];
  @tracked isLoadingAccounts = true;
  @tracked socialProviders: string[] = [];
  @tracked linkedOAuthAccounts: OAuthAccount[] = [];
  @tracked isLoadingSocial = true;
  @tracked watchRegion = '';
  @tracked isSavingRegion = false;
  @tracked streamingServices: number[] = [];
  @tracked availableProviders: Array<{
    providerId: number;
    providerName: string;
    logoPath: string;
  }> = [];
  @tracked isLoadingProviders = false;
  @tracked isSavingServices = false;

  get userName(): string {
    return this.session.user?.name ?? '';
  }

  get userEmail(): string {
    return this.session.user?.email ?? '';
  }

  get oauthCallbackUrl(): string {
    return (config.favoris.appUrl || window.location.origin) + '/auto-login';
  }

  get enrichedSocialProviders(): Array<{
    id: string;
    label: string;
    icon: string;
    isLinked: boolean;
  }> {
    return this.socialProviders.map((provider) => {
      const isLinked = this.linkedOAuthAccounts.some(
        (a) => a.providerId === provider
      );
      return {
        id: provider,
        label: SOCIAL_PROVIDER_LABELS[provider] ?? provider,
        icon: SOCIAL_PROVIDER_ICONS[provider] ?? ShieldIcon,
        isLinked,
      };
    });
  }

  constructor(owner: Owner, args: Record<string, never>) {
    super(owner, args);
    void this.loadLinkedAccounts();
    void this.loadSocialData();
    void this.loadPreferences();
    void this.loadPushStatus();
    void this.loadLocationStatus();
  }

  @action
  async loadLinkedAccounts() {
    this.isLoadingAccounts = true;
    try {
      const headers = this.session.authHeader
        ? { Authorization: this.session.authHeader }
        : undefined;
      const response = await apiFetch('/api/me/linked-accounts', {
        headers,
      });
      if (response.ok) {
        const json = (await response.json()) as {
          data?: LinkedAccountData[];
        };
        this.linkedAccounts = json.data ?? [];
      }
    } catch {
      // Silently fail — linked accounts are not critical
    } finally {
      this.isLoadingAccounts = false;
    }
  }

  @action
  async unlinkAccount(id: string) {
    const headers: Record<string, string> = {};
    if (this.session.authHeader) {
      headers['Authorization'] = this.session.authHeader;
    }
    const response = await apiFetch(
      `/api/me/linked-accounts/${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
        headers,
      }
    );
    if (response.ok || response.status === 204) {
      this.linkedAccounts = this.linkedAccounts.filter((a) => a.id !== id);
    }
  }

  @action
  async loadSocialData() {
    this.isLoadingSocial = true;
    try {
      const provRes = await apiFetch('/api/auth/providers');
      if (provRes.ok) {
        const { providers } = (await provRes.json()) as { providers: string[] };
        this.socialProviders = providers;
      }
      if (this.socialProviders.length > 0) {
        const headers = this.session.authHeader
          ? { Authorization: this.session.authHeader }
          : undefined;
        const accRes = await apiFetch('/api/auth/list-accounts', { headers });
        if (accRes.ok) {
          this.linkedOAuthAccounts = (await accRes.json()) as OAuthAccount[];
        }
      }
    } catch {
      // Silently fail — social login is not critical
    } finally {
      this.isLoadingSocial = false;
    }
  }

  @action
  async connectSocial(provider: string) {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (this.session.authHeader) {
        headers['Authorization'] = this.session.authHeader;
      }
      const res = await apiFetch('/api/auth/link-social', {
        method: 'POST',
        headers,
        body: JSON.stringify({ provider, callbackURL: this.oauthCallbackUrl }),
      });
      if (res.ok) {
        const data = (await res.json()) as { url?: string };
        if (data.url) {
          const { Capacitor } = await import('@capacitor/core');
          if (Capacitor.isNativePlatform()) {
            const { Browser } = await import('@capacitor/browser');
            await Browser.open({ url: data.url });
          } else {
            window.location.href = data.url;
          }
        }
      }
    } catch {
      // Silently fail
    }
  }

  @action
  async disconnectSocial(providerId: string) {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (this.session.authHeader) {
        headers['Authorization'] = this.session.authHeader;
      }
      const res = await apiFetch('/api/auth/unlink-account', {
        method: 'POST',
        headers,
        body: JSON.stringify({ providerId }),
      });
      if (res.ok) {
        this.linkedOAuthAccounts = this.linkedOAuthAccounts.filter(
          (a) => a.providerId !== providerId
        );
      }
    } catch {
      // Silently fail
    }
  }

  @action
  async logout() {
    await this.session.invalidate();
  }

  @action
  async loadPreferences() {
    try {
      const headers = this.session.authHeader
        ? { Authorization: this.session.authHeader }
        : undefined;
      const response = await apiFetch('/api/me/preferences', { headers });
      if (response.ok) {
        const json = (await response.json()) as {
          data?: {
            attributes?: { watchRegion?: string; streamingServices?: number[] };
          };
        };
        this.watchRegion = json.data?.attributes?.watchRegion ?? '';
        this.streamingServices = json.data?.attributes?.streamingServices ?? [];
        if (this.watchRegion) {
          void this.loadAvailableProviders(this.watchRegion);
        }
      }
    } catch {
      // Silently fail
    }
  }

  @action
  async loadAvailableProviders(region: string) {
    if (!region) return;
    this.isLoadingProviders = true;
    try {
      const response = await apiFetch(
        `/api/watch-providers?region=${encodeURIComponent(region)}`
      );
      if (response.ok) {
        const json = (await response.json()) as {
          data?: Array<{
            providerId: number;
            providerName: string;
            logoPath: string;
          }>;
        };
        this.availableProviders = json.data ?? [];
      }
    } catch {
      // Silently fail
    } finally {
      this.isLoadingProviders = false;
    }
  }

  @action
  async toggleStreamingService(providerId: number, event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    const next = checked
      ? [...this.streamingServices, providerId]
      : this.streamingServices.filter((id) => id !== providerId);
    this.streamingServices = next;
    this.isSavingServices = true;
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (this.session.authHeader) {
        headers['Authorization'] = this.session.authHeader;
      }
      await apiFetch('/api/me/preferences', {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ streamingServices: next }),
      });
    } catch {
      // Silently fail
    } finally {
      this.isSavingServices = false;
    }
  }

  @action
  async saveWatchRegion(event: Event) {
    const target = event.target as HTMLSelectElement;
    const region = target.value;
    this.watchRegion = region;
    this.isSavingRegion = true;
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (this.session.authHeader) {
        headers['Authorization'] = this.session.authHeader;
      }
      await apiFetch('/api/me/preferences', {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ watchRegion: region }),
      });
      if (region) {
        void this.loadAvailableProviders(region);
      } else {
        this.availableProviders = [];
      }
    } catch {
      // Silently fail
    } finally {
      this.isSavingRegion = false;
    }
  }

  @action
  async loadPushStatus() {
    const userId = this.session.user?.id;
    if (!userId) return;
    try {
      this.isNativePush = await isNativePlatform();
      this.pushStatus = await this.pushNotifications.getPushStatus(userId);
    } catch {
      this.pushStatus = 'unsupported';
    }
  }

  @action
  async togglePushNotifications(event: Event) {
    const userId = this.session.user?.id;
    if (!userId) return;
    const checked = (event.target as HTMLInputElement).checked;
    this.isTogglingPush = true;
    try {
      if (checked) {
        await this.pushNotifications.requestPermission(userId);
        if (this.isNativePush) {
          // FCM token registration completes asynchronously via the
          // 'registration' listener — give it a moment before re-checking.
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } else {
        await this.pushNotifications.unsubscribe(userId);
      }
    } finally {
      await this.loadPushStatus();
      this.isTogglingPush = false;
    }
  }

  @action
  async openNotificationSettings() {
    await this.pushNotifications.openNativeSettings();
  }

  @action
  async loadLocationStatus() {
    try {
      this.isNativeLocation = await isNativePlatform();
      this.locationStatus = await this.location.getLocationStatus();
    } catch {
      this.locationStatus = 'unsupported';
    }
  }

  @action
  async toggleLocation(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.isTogglingLocation = true;
    try {
      if (checked) {
        await this.location.fetchLocation();
      } else {
        this.location.clearLocation();
      }
    } finally {
      await this.loadLocationStatus();
      this.isTogglingLocation = false;
    }
  }

  @action
  async openLocationSettings() {
    await this.location.openNativeSettings();
  }

  platformLabel = (platform: string) => PLATFORM_LABELS[platform] ?? platform;

  formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  <template>
    <div class="mx-auto max-w-lg">
      <MobileHeader @title="My Account" />
      <h1 class="mb-6 hidden text-3xl font-bold lg:block">My Account</h1>

      {{! Profile card }}
      <div class="card bg-base-100 mb-6 shadow">
        <div class="card-body">
          <div class="flex items-center gap-4">
            <div
              class="bg-base-300 flex h-16 w-16 items-center justify-center rounded-full"
            >
              <Icon @icon={{UserIcon}} class="h-8 w-8" />
            </div>
            <div class="min-w-0 flex-1">
              {{#if this.userName}}
                <h2 class="truncate text-xl font-semibold">
                  {{this.userName}}
                </h2>
              {{/if}}
              <p
                class="text-base-content/60 flex items-center gap-1 truncate text-sm"
              >
                <Icon @icon={{MailIcon}} class="h-4 w-4 shrink-0" />
                {{this.userEmail}}
              </p>
            </div>
          </div>
        </div>
      </div>

      {{! Linked Accounts }}
      <div class="card bg-base-100 mb-6 shadow">
        <div class="card-body">
          <h3 class="card-title text-base">
            <Icon @icon={{LinkIcon}} class="h-5 w-5" />
            Linked Accounts
          </h3>
          <p class="text-base-content/70 mb-2 text-sm">
            Chat platform accounts linked to your Favoris account. Send a
            message to the bot to link a new account.
          </p>

          {{#if this.isLoadingAccounts}}
            <div class="flex justify-center py-4">
              <span class="loading loading-spinner loading-sm"></span>
            </div>
          {{else if this.linkedAccounts.length}}
            <div class="space-y-3">
              {{#each this.linkedAccounts as |account|}}
                <div
                  class="bg-base-200 flex items-center justify-between rounded-lg p-3"
                >
                  <div>
                    <span class="font-medium">
                      {{this.platformLabel account.attributes.platform}}
                    </span>
                    {{#if account.attributes.platformUsername}}
                      <span class="text-base-content/60 ml-1 text-sm">
                        @{{account.attributes.platformUsername}}
                      </span>
                    {{/if}}
                    {{#if account.attributes.platformDisplayName}}
                      <span class="text-base-content/60 ml-1 text-sm">
                        ({{account.attributes.platformDisplayName}})
                      </span>
                    {{/if}}
                    <p class="text-base-content/50 text-xs">
                      Linked
                      {{this.formatDate account.attributes.linkedAt}}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm text-error"
                    title="Unlink account"
                    {{on "click" (fn this.unlinkAccount account.id)}}
                  >
                    <Icon @icon={{UnlinkIcon}} class="h-4 w-4" />
                  </button>
                </div>
              {{/each}}
            </div>
          {{else}}
            <p class="text-base-content/50 py-2 text-sm italic">
              No linked accounts yet.
            </p>
          {{/if}}
        </div>
      </div>

      {{! Social Login }}
      {{#if this.enrichedSocialProviders.length}}
        <div class="card bg-base-100 mb-6 shadow">
          <div class="card-body">
            <h3 class="card-title text-base">
              <Icon @icon={{ShieldIcon}} class="h-5 w-5" />
              Social Login
            </h3>
            <p class="text-base-content/70 mb-2 text-sm">
              Connect social accounts to sign in without a password.
            </p>
            {{#if this.isLoadingSocial}}
              <div class="flex justify-center py-4">
                <span class="loading loading-spinner loading-sm"></span>
              </div>
            {{else}}
              <div class="space-y-3">
                {{#each this.enrichedSocialProviders as |meta|}}
                  <div
                    class="bg-base-200 flex items-center justify-between rounded-lg p-3"
                  >
                    <div class="flex items-center gap-3">
                      <Icon @icon={{meta.icon}} class="h-5 w-5 shrink-0" />
                      <div>
                        <span class="font-medium">{{meta.label}}</span>
                        {{#if meta.isLinked}}
                          <span class="badge badge-success badge-sm ml-2">
                            Connected
                          </span>
                        {{/if}}
                      </div>
                    </div>
                    {{#if meta.isLinked}}
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm text-error"
                        title="Disconnect"
                        {{on "click" (fn this.disconnectSocial meta.id)}}
                      >
                        <Icon @icon={{UnlinkIcon}} class="h-4 w-4" />
                        Disconnect
                      </button>
                    {{else}}
                      <button
                        type="button"
                        class="btn btn-outline btn-sm gap-2"
                        {{on "click" (fn this.connectSocial meta.id)}}
                      >
                        <Icon @icon={{LinkIcon}} class="h-4 w-4" />
                        Connect
                      </button>
                    {{/if}}
                  </div>
                {{/each}}
              </div>
            {{/if}}
          </div>
        </div>
      {{/if}}

      {{! Language }}
      <div class="card bg-base-100 mb-6 shadow">
        <div class="card-body">
          <h3 class="card-title text-base">Language</h3>
          <div class="flex items-center justify-between">
            <span class="text-base-content/70 text-sm">
              Choose your preferred content language
            </span>
            <LanguageSelector />
          </div>
        </div>
      </div>

      {{! Theme }}
      <div class="card bg-base-100 mb-6 shadow">
        <div class="card-body">
          <h3 class="card-title text-base">Theme</h3>
          <div class="flex items-center justify-between">
            <span class="text-base-content/70 text-sm">
              Choose your preferred color theme
            </span>
            <ThemeSelector />
          </div>
        </div>
      </div>

      {{! Push Notifications }}
      <div class="card bg-base-100 mb-6 shadow">
        <div class="card-body">
          <h3 class="card-title text-base">
            <Icon @icon={{BellIcon}} class="h-5 w-5" />
            Push Notifications
          </h3>
          <p class="text-base-content/70 mb-2 text-sm">
            Get notified when your extraction is ready.
          </p>
          {{#if (eq this.pushStatus "loading")}}
            <span class="loading loading-spinner loading-sm"></span>
          {{else if (eq this.pushStatus "unsupported")}}
            <p class="text-base-content/50 text-sm italic">
              Not supported in this browser.
            </p>
          {{else if (eq this.pushStatus "blocked")}}
            <p class="text-base-content/50 text-sm italic">
              Blocked — enable notifications for this
              {{if this.isNativePush "app" "site"}}
              in your
              {{if this.isNativePush "device" "browser"}}
              settings, then reload this page.
            </p>
            {{#if this.isNativePush}}
              <button
                type="button"
                class="btn btn-outline btn-sm mt-2"
                {{on "click" this.openNotificationSettings}}
              >
                Open Settings
              </button>
            {{/if}}
          {{else}}
            <label class="label cursor-pointer gap-2">
              <input
                type="checkbox"
                class="toggle toggle-sm toggle-primary"
                checked={{eq this.pushStatus "on"}}
                disabled={{this.isTogglingPush}}
                {{on "change" this.togglePushNotifications}}
              />
              <span class="label-text">
                {{if (eq this.pushStatus "on") "Enabled" "Disabled"}}
              </span>
              {{#if this.isTogglingPush}}
                <span class="loading loading-spinner loading-xs"></span>
              {{/if}}
            </label>
          {{/if}}
        </div>
      </div>

      {{! Location }}
      <div class="card bg-base-100 mb-6 shadow">
        <div class="card-body">
          <h3 class="card-title text-base">
            <Icon @icon={{MapPinIcon}} class="h-5 w-5" />
            Location
          </h3>
          <p class="text-base-content/70 mb-2 text-sm">
            Used to show distance to nearby places.
          </p>
          {{#if (eq this.locationStatus "loading")}}
            <span class="loading loading-spinner loading-sm"></span>
          {{else if (eq this.locationStatus "unsupported")}}
            <p class="text-base-content/50 text-sm italic">
              Not supported on this device.
            </p>
          {{else if (eq this.locationStatus "blocked")}}
            <p class="text-base-content/50 text-sm italic">
              Blocked — enable location for this
              {{if this.isNativeLocation "app" "site"}}
              in your
              {{if this.isNativeLocation "device" "browser"}}
              settings, then reload this page.
            </p>
            {{#if this.isNativeLocation}}
              <button
                type="button"
                class="btn btn-outline btn-sm mt-2"
                {{on "click" this.openLocationSettings}}
              >
                Open Settings
              </button>
            {{/if}}
          {{else}}
            <label class="label cursor-pointer gap-2">
              <input
                type="checkbox"
                class="toggle toggle-sm toggle-primary"
                checked={{eq this.locationStatus "on"}}
                disabled={{this.isTogglingLocation}}
                {{on "change" this.toggleLocation}}
              />
              <span class="label-text">
                {{if (eq this.locationStatus "on") "Enabled" "Disabled"}}
              </span>
              {{#if this.isTogglingLocation}}
                <span class="loading loading-spinner loading-xs"></span>
              {{/if}}
            </label>
          {{/if}}
        </div>
      </div>

      {{! Streaming Region }}
      <div class="card bg-base-100 mb-6 shadow">
        <div class="card-body">
          <h3 class="card-title text-base">
            <Icon @icon={{TvIcon}} class="h-5 w-5" />
            Streaming Region
          </h3>
          <p class="text-base-content/70 mb-2 text-sm">
            Select your country to see where movies and TV shows are available
            to stream, rent, or buy.
          </p>
          <div class="flex items-center gap-2">
            <select
              class="select select-bordered flex-1"
              aria-label="Streaming region"
              {{on "change" this.saveWatchRegion}}
            >
              <option value="" selected={{eq this.watchRegion ""}}>Select a
                region…</option>
              <option value="AR" selected={{eq this.watchRegion "AR"}}>🇦🇷
                Argentina</option>
              <option value="AU" selected={{eq this.watchRegion "AU"}}>🇦🇺
                Australia</option>
              <option value="AT" selected={{eq this.watchRegion "AT"}}>🇦🇹
                Austria</option>
              <option value="BE" selected={{eq this.watchRegion "BE"}}>🇧🇪
                Belgium</option>
              <option value="BR" selected={{eq this.watchRegion "BR"}}>🇧🇷 Brazil</option>
              <option value="CA" selected={{eq this.watchRegion "CA"}}>🇨🇦 Canada</option>
              <option value="CL" selected={{eq this.watchRegion "CL"}}>🇨🇱 Chile</option>
              <option value="CO" selected={{eq this.watchRegion "CO"}}>🇨🇴
                Colombia</option>
              <option value="CZ" selected={{eq this.watchRegion "CZ"}}>🇨🇿 Czech
                Republic</option>
              <option value="DK" selected={{eq this.watchRegion "DK"}}>🇩🇰
                Denmark</option>
              <option value="FI" selected={{eq this.watchRegion "FI"}}>🇫🇮
                Finland</option>
              <option value="FR" selected={{eq this.watchRegion "FR"}}>🇫🇷 France</option>
              <option value="DE" selected={{eq this.watchRegion "DE"}}>🇩🇪
                Germany</option>
              <option value="GR" selected={{eq this.watchRegion "GR"}}>🇬🇷 Greece</option>
              <option value="HU" selected={{eq this.watchRegion "HU"}}>🇭🇺
                Hungary</option>
              <option value="IN" selected={{eq this.watchRegion "IN"}}>🇮🇳 India</option>
              <option value="ID" selected={{eq this.watchRegion "ID"}}>🇮🇩
                Indonesia</option>
              <option value="IE" selected={{eq this.watchRegion "IE"}}>🇮🇪
                Ireland</option>
              <option value="IL" selected={{eq this.watchRegion "IL"}}>🇮🇱 Israel</option>
              <option value="IT" selected={{eq this.watchRegion "IT"}}>🇮🇹 Italy</option>
              <option value="JP" selected={{eq this.watchRegion "JP"}}>🇯🇵 Japan</option>
              <option value="KR" selected={{eq this.watchRegion "KR"}}>🇰🇷 South
                Korea</option>
              <option value="MY" selected={{eq this.watchRegion "MY"}}>🇲🇾
                Malaysia</option>
              <option value="MX" selected={{eq this.watchRegion "MX"}}>🇲🇽 Mexico</option>
              <option value="NL" selected={{eq this.watchRegion "NL"}}>🇳🇱
                Netherlands</option>
              <option value="NZ" selected={{eq this.watchRegion "NZ"}}>🇳🇿 New
                Zealand</option>
              <option value="NO" selected={{eq this.watchRegion "NO"}}>🇳🇴 Norway</option>
              <option value="PE" selected={{eq this.watchRegion "PE"}}>🇵🇪 Peru</option>
              <option value="PH" selected={{eq this.watchRegion "PH"}}>🇵🇭
                Philippines</option>
              <option value="PL" selected={{eq this.watchRegion "PL"}}>🇵🇱 Poland</option>
              <option value="PT" selected={{eq this.watchRegion "PT"}}>🇵🇹
                Portugal</option>
              <option value="RO" selected={{eq this.watchRegion "RO"}}>🇷🇴
                Romania</option>
              <option value="RU" selected={{eq this.watchRegion "RU"}}>🇷🇺 Russia</option>
              <option value="SA" selected={{eq this.watchRegion "SA"}}>🇸🇦 Saudi
                Arabia</option>
              <option value="SG" selected={{eq this.watchRegion "SG"}}>🇸🇬
                Singapore</option>
              <option value="ZA" selected={{eq this.watchRegion "ZA"}}>🇿🇦 South
                Africa</option>
              <option value="ES" selected={{eq this.watchRegion "ES"}}>🇪🇸 Spain</option>
              <option value="SE" selected={{eq this.watchRegion "SE"}}>🇸🇪 Sweden</option>
              <option value="CH" selected={{eq this.watchRegion "CH"}}>🇨🇭
                Switzerland</option>
              <option value="TW" selected={{eq this.watchRegion "TW"}}>🇹🇼 Taiwan</option>
              <option value="TH" selected={{eq this.watchRegion "TH"}}>🇹🇭
                Thailand</option>
              <option value="TR" selected={{eq this.watchRegion "TR"}}>🇹🇷 Turkey</option>
              <option value="AE" selected={{eq this.watchRegion "AE"}}>🇦🇪 United
                Arab Emirates</option>
              <option value="GB" selected={{eq this.watchRegion "GB"}}>🇬🇧 United
                Kingdom</option>
              <option value="US" selected={{eq this.watchRegion "US"}}>🇺🇸 United
                States</option>
              <option value="VE" selected={{eq this.watchRegion "VE"}}>🇻🇪
                Venezuela</option>
            </select>
            {{#if this.isSavingRegion}}
              <span class="loading loading-spinner loading-xs"></span>
            {{/if}}
          </div>
        </div>
      </div>

      {{! Streaming Services }}
      <div class="card bg-base-100 mb-6 shadow">
        <div class="card-body">
          <h3 class="card-title text-base">
            <Icon @icon={{TvIcon}} class="h-5 w-5" />
            Streaming Services
          </h3>
          <p class="text-base-content/70 mb-2 text-sm">
            Select the services you subscribe to — they appear first on movie
            and show pages.
          </p>
          {{#if this.watchRegion}}
            {{#if this.isLoadingProviders}}
              <div class="flex items-center gap-2 py-2">
                <span class="loading loading-spinner loading-xs"></span>
                <span class="text-base-content/60 text-sm">Loading providers…</span>
              </div>
            {{else if this.availableProviders.length}}
              <div class="flex flex-col gap-1">
                {{#each this.availableProviders as |provider|}}
                  <label
                    class="label cursor-pointer justify-start gap-3 py-1.5"
                  >
                    <input
                      type="checkbox"
                      class="checkbox checkbox-sm checkbox-primary"
                      checked={{includesId
                        this.streamingServices
                        provider.providerId
                      }}
                      {{on
                        "change"
                        (fn this.toggleStreamingService provider.providerId)
                      }}
                    />
                    <img
                      src={{tmdbProviderLogo provider.logoPath}}
                      alt={{provider.providerName}}
                      class="h-6 w-6 rounded"
                      loading="lazy"
                    />
                    <span
                      class="label-text text-sm"
                    >{{provider.providerName}}</span>
                    {{#if this.isSavingServices}}
                      <span
                        class="loading loading-spinner loading-xs ml-auto"
                      ></span>
                    {{/if}}
                  </label>
                {{/each}}
              </div>
            {{else}}
              <p class="text-base-content/50 text-sm">No providers found for
                your region.</p>
            {{/if}}
          {{else}}
            <p class="text-base-content/50 text-sm">Set your streaming region
              above to see available services.</p>
          {{/if}}
        </div>
      </div>

      {{#if this.session.isModerator}}
        <div class="card bg-base-100 mb-6 shadow">
          <div class="card-body">
            <h3 class="card-title text-base">Administration</h3>
            <p class="text-base-content/70 mb-2 text-sm">
              You have
              {{if this.session.isAdmin "admin" "moderator"}}
              access.
            </p>
            <a href="/admin/" class="btn btn-outline gap-2">
              <Icon @icon={{ShieldIcon}} class="h-5 w-5" />
              Open Admin Panel
            </a>
          </div>
        </div>
      {{/if}}

      {{! Logout }}
      <button
        type="button"
        class="btn btn-error btn-outline w-full gap-2"
        {{on "click" this.logout}}
      >
        <Icon @icon={{LogOutIcon}} class="h-5 w-5" />
        Logout
      </button>
    </div>
  </template>
}
